import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Clock, CheckCircle2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

// Refresh Radar — one dashboard listing every course / lesson whose
// `review_by` is overdue or falls inside the configurable window.
// Sorted by urgency (overdue → critical → soon → healthy) so admins
// can burn through the backlog fastest-first.

const URGENCY_META = {
  overdue: { label: 'Overdue',  cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200 border-rose-500/40',  Icon: AlertTriangle },
  critical:{ label: 'Critical', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-500/40', Icon: Clock },
  soon:    { label: 'Soon',     cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200 border-sky-500/40',      Icon: Clock },
  healthy: { label: 'Healthy',  cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-500/40', Icon: CheckCircle2 },
  unknown: { label: 'Unknown',  cls: 'bg-slate-200 text-slate-700 border-slate-400/40', Icon: Clock },
};

const fmt = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(v); }
};

const RefreshRadar = () => {
  const { API } = useContext(AuthContext);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);
  const [includeHealthy, setIncludeHealthy] = useState(false);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/lms/refresh-radar`, {
        params: { window_days: windowDays, include_healthy: includeHealthy },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Refresh Radar');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays, includeHealthy]);

  const items = useMemo(() => {
    let list = data.items || [];
    if (kindFilter !== 'all') list = list.filter((it) => it.kind === kindFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((it) =>
        (it.title || '').toLowerCase().includes(q) ||
        (it.course_title || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data, kindFilter, search]);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="refresh-radar-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Refresh Radar</h1>
          <p className="text-sm text-muted-foreground">
            Every course and lesson whose review date is nearing — sorted by urgency so nothing stale slips through.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-radar-reload">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {/* Bucket summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="refresh-radar-counts">
        {['overdue', 'critical', 'soon', 'healthy', 'unknown'].map((k) => {
          const meta = URGENCY_META[k];
          const n = data.counts?.[k] || 0;
          return (
            <div key={k} className={`rounded-lg border px-3 py-2 ${meta.cls}`}>
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest">
                <meta.Icon className="w-3 h-3" /> {meta.label}
              </div>
              <div className="text-2xl font-black leading-tight">{n}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 border border-border rounded-md px-2 py-1.5 bg-background">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title / course..."
            className="bg-transparent text-sm focus:outline-none w-52"
            data-testid="refresh-radar-search"
          />
        </div>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
          data-testid="refresh-radar-kind"
        >
          <option value="all">All kinds</option>
          <option value="course">Courses only</option>
          <option value="lesson">Lessons only</option>
        </select>
        <select
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          className="text-sm border border-border rounded-md px-2 py-1.5 bg-background"
          data-testid="refresh-radar-window"
        >
          <option value={7}>Due in 7 days</option>
          <option value={14}>Due in 14 days</option>
          <option value={30}>Due in 30 days</option>
          <option value={90}>Due in 90 days</option>
          <option value={365}>Due in 1 year</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={includeHealthy}
            onChange={(e) => setIncludeHealthy(e.target.checked)}
            data-testid="refresh-radar-include-healthy"
          />
          Also show healthy items
        </label>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="refresh-radar-table">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left p-3">Urgency</th>
                <th className="text-left p-3">Kind</th>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Course / Module</th>
                <th className="text-left p-3">Review by</th>
                <th className="text-left p-3">Published</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                  Nothing stale in this window — nice work.
                </td></tr>
              )}
              {items.map((it) => {
                const meta = URGENCY_META[it.urgency] || URGENCY_META.unknown;
                return (
                  <tr key={`${it.kind}-${it.id}`} className="border-t border-border hover:bg-muted/20"
                      data-testid={`radar-row-${it.id}`}>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest border ${meta.cls}`}>
                        <meta.Icon className="w-3 h-3" />
                        {meta.label}
                        {typeof it.days_until === 'number' && (
                          <span className="ml-1 opacity-80">
                            {it.days_until < 0 ? `${Math.abs(it.days_until)}d late` : `${it.days_until}d`}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="p-3 capitalize">{it.kind}</td>
                    <td className="p-3 font-medium">{it.title}</td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {it.kind === 'lesson' ? (
                        <>
                          {it.course_title || '—'}
                          {it.parent_title ? <span className="opacity-70"> · {it.parent_title}</span> : null}
                        </>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-xs">{fmt(it.review_by)}</td>
                    <td className="p-3 text-xs">{fmt(it.published_at)}</td>
                    <td className="p-3 text-right">
                      <Link
                        to={it.kind === 'lesson' && it.course_id
                          ? `/admin/lms/course/${it.course_id}/workspace`
                          : `/admin/lms`}
                        className="text-primary text-xs font-semibold hover:underline"
                        data-testid={`radar-open-${it.id}`}
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RefreshRadar;
